import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ShoppingBagIcon, DocumentTextIcon, ClipboardDocumentListIcon,
  ArchiveBoxIcon, TruckIcon, BanknotesIcon, UserGroupIcon, ChartBarIcon,
  Cog6ToothIcon, Bars3Icon, XMarkIcon, BuildingStorefrontIcon,
  DocumentDuplicateIcon, CurrencyDollarIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../contexts/AuthContext';
import { apiGet } from '../../../utils/api';

interface MarketplaceAccessStatus {
  hasMembership: boolean;
  organisationType: 'buyer' | 'supplier' | 'both' | null;
  hasProfile: boolean;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: string[];
}

const BUYER_NAV: NavItem[] = [
  { label: 'Overview', path: '/marketplace/buyer', icon: <ChartBarIcon className="w-5 h-5" /> },
  { label: 'Marketplace', path: '/marketplace/buyer/catalogue', icon: <ShoppingBagIcon className="w-5 h-5" /> },
  { label: 'Orders', path: '/marketplace/buyer/orders', icon: <DocumentDuplicateIcon className="w-5 h-5" /> },
  { label: 'RFQs', path: '/marketplace/buyer/rfqs', icon: <DocumentTextIcon className="w-5 h-5" /> },
  { label: 'Quotations', path: '/marketplace/buyer/quotations', icon: <ClipboardDocumentListIcon className="w-5 h-5" /> },
  { label: 'Deliveries', path: '/marketplace/buyer/deliveries', icon: <TruckIcon className="w-5 h-5" /> },
  { label: 'Invoices', path: '/marketplace/buyer/invoices', icon: <BanknotesIcon className="w-5 h-5" /> },
  { label: 'Suppliers', path: '/marketplace/buyer/suppliers', icon: <UserGroupIcon className="w-5 h-5" /> },
  { label: 'Organisation', path: '/marketplace/buyer/organisation', icon: <BuildingStorefrontIcon className="w-5 h-5" /> },
  { label: 'Profile & Settings', path: '/marketplace/buyer/profile', icon: <Cog6ToothIcon className="w-5 h-5" /> },
];

const SUPPLIER_NAV: NavItem[] = [
  { label: 'Overview', path: '/marketplace/supplier', icon: <ChartBarIcon className="w-5 h-5" /> },
  { label: 'My Listings', path: '/marketplace/supplier/listings', icon: <ArchiveBoxIcon className="w-5 h-5" /> },
  { label: 'RFQ Opportunities', path: '/marketplace/supplier/rfqs', icon: <DocumentTextIcon className="w-5 h-5" /> },
  { label: 'Quotations', path: '/marketplace/supplier/quotations', icon: <ClipboardDocumentListIcon className="w-5 h-5" /> },
  { label: 'Orders', path: '/marketplace/supplier/orders', icon: <DocumentDuplicateIcon className="w-5 h-5" /> },
  { label: 'Fulfilment', path: '/marketplace/supplier/fulfilment', icon: <TruckIcon className="w-5 h-5" /> },
  { label: 'Inventory', path: '/marketplace/supplier/inventory', icon: <ArchiveBoxIcon className="w-5 h-5" /> },
  { label: 'Settlements', path: '/marketplace/supplier/settlements', icon: <CurrencyDollarIcon className="w-5 h-5" /> },
];

const ADMIN_NAV: NavItem[] = [
  { label: 'Marketplace Overview', path: '/marketplace/admin', icon: <ChartBarIcon className="w-5 h-5" /> },
  { label: 'Supplier Verification', path: '/marketplace/admin/suppliers', icon: <ShieldCheckIcon className="w-5 h-5" /> },
  { label: 'Catalogue', path: '/marketplace/admin/catalogue', icon: <ShoppingBagIcon className="w-5 h-5" /> },
  { label: 'All Orders', path: '/marketplace/admin/orders', icon: <DocumentDuplicateIcon className="w-5 h-5" /> },
  { label: 'Settlements', path: '/marketplace/admin/settlements', icon: <CurrencyDollarIcon className="w-5 h-5" /> },
  { label: 'Commission Rules', path: '/marketplace/admin/commission', icon: <Cog6ToothIcon className="w-5 h-5" /> },
  { label: 'Disputes', path: '/marketplace/admin/disputes', icon: <BanknotesIcon className="w-5 h-5" /> },
];

interface MarketplaceShellProps {
  children: React.ReactNode;
  activeTab?: 'buyer' | 'supplier' | 'admin';
  onTabChange?: (tab: 'buyer' | 'supplier' | 'admin') => void;
}

const MarketplaceShell: React.FC<MarketplaceShellProps> = ({ children, activeTab = 'buyer', onTabChange }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketplaceType, setMarketplaceType] = useState<'buyer' | 'supplier' | 'both' | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch marketplace membership type
  useEffect(() => {
    if (isSuperAdmin) {
      setMarketplaceType('both');
      return;
    }

    const fetchMembershipType = async () => {
      try {
        // Use the lightweight membership-status endpoint first
        const statusData = await apiGet<MarketplaceAccessStatus>('/api/marketplace/b2b/membership-status');

        if (statusData.hasMembership) {
          const orgType = statusData.organisationType;
          if (orgType === 'buyer') {
            setMarketplaceType('buyer');
          } else if (orgType === 'both') {
            setMarketplaceType('both');
          } else {
            setMarketplaceType('supplier');
          }
        } else if (statusData.hasProfile) {
          // Has profile but no membership yet - show supplier side
          setMarketplaceType('supplier');
        } else {
          setMarketplaceType(null);
        }
      } catch (err) {
        console.error('Failed to fetch marketplace membership:', err);
      }
    };

    fetchMembershipType();
  }, [user, isSuperAdmin]);

  // Determine available tabs based on membership
  const getAvailableTabs = () => {
    const tabs = [];

    if (marketplaceType === 'buyer' || marketplaceType === 'both') {
      tabs.push({ key: 'buyer' as const, label: 'Buyer Procurement' });
    }

    if (marketplaceType === 'supplier' || marketplaceType === 'both') {
      tabs.push({ key: 'supplier' as const, label: 'Supplier Portal' });
    }

    if (isSuperAdmin) {
      tabs.push({ key: 'admin' as const, label: 'Platform Admin' });
    }

    return tabs;
  };

  const tabs = getAvailableTabs();

  const currentNav = activeTab === 'admin' && isSuperAdmin
    ? ADMIN_NAV
    : activeTab === 'supplier'
      ? SUPPLIER_NAV
      : BUYER_NAV;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-60' : 'w-0'} hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-200 overflow-hidden`}>
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <BuildingStorefrontIcon className="w-6 h-6 text-emerald-600" />
            <span className="font-bold text-gray-900 text-sm">eSanza Market</span>
          </div>
        </div>

        {/* Role tabs */}
        <div className="px-2 pt-3 pb-1">
          <div className="flex flex-col gap-0.5">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  if (onTabChange) {
                    onTabChange(tab.key);
                  } else {
                    const basePath = tab.key === 'admin'
                      ? '/marketplace/admin'
                      : `/marketplace/${tab.key}`;
                    navigate(basePath);
                  }
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded text-left transition-colors ${
                  activeTab === tab.key
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {currentNav.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {item.icon}
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Tenant context */}
        <div className="p-3 border-t border-gray-100">
          <div className="text-xs text-gray-400 truncate">
            {user?.tenant?.name || 'Platform'}
          </div>
          <div className="text-xs font-medium text-gray-600 truncate">
            {user?.full_name || user?.email}
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl z-50 overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <BuildingStorefrontIcon className="w-6 h-6 text-emerald-600" />
                <span className="font-bold text-gray-900">eSanza Market</span>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-gray-400">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <nav className="p-2 space-y-0.5">
              {currentNav.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-lg ${
                      isActive ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:block text-gray-400 hover:text-gray-600"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden text-gray-400 hover:text-gray-600"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>

          <h1 className="text-base font-semibold text-gray-900">
            {tabs.find(t => t.key === activeTab)?.label || 'Marketplace'}
          </h1>

          <div className="ml-auto flex items-center gap-3 text-sm">
            <span className="text-gray-500 text-xs hidden md:block">
              {user?.tenant?.name}
            </span>
            <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs font-medium">
              {user?.role?.replace('_', ' ')}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MarketplaceShell;
